// Shared seed-guard for eager-mounted tracking form modals. These modals
// never unmount — the parent tab always renders them and only toggles
// `isOpen` — so a plain `useState(buildInitialState())` lazy initializer
// only runs once, on the modal's first mount, and never again. Without a
// guard, editing a different row (or cancelling a create and reopening it)
// would keep showing whatever was last typed.
//
// The fix is a render-time seed-key comparison (no `useEffect`, so there's
// no extra render or setState-in-effect cascade): `seedKey` identifies what
// should currently be seeded (e.g. `${mode}:${source?.id ?? "new"}`), and is
// only meaningful while `isOpen` — closing collapses it to a sentinel so the
// guard resets. That reset-on-close step is the part a naive port easily
// drops: without it, Cancel out of a create form and reopening it (same
// mode, no source — an identical seedKey) is indistinguishable from "still
// open, nothing changed", so the stale typed values survive the reopen.
//
// `onReseed` fires synchronously in the same render-time branch as the
// seed, so callers can reset sibling state (field errors, submit error)
// that they still own directly, without needing to know internally when a
// reseed occurred.

import { useState, type Dispatch, type SetStateAction } from "react";

const CLOSED = Symbol("useSeededFormState.closed");

export default function useSeededFormState<S>(
  isOpen: boolean,
  seedKey: string,
  buildState: () => S,
  onReseed?: () => void,
): [S, Dispatch<SetStateAction<S>>] {
  const [state, setState] = useState<S>(buildState);
  // Lazily seeded to the mount-time effective key (rather than always
  // CLOSED) so a modal that happens to mount already open doesn't
  // immediately re-run buildState() a second time on its first render —
  // the lazy `state` initializer above already covers that case.
  const [seededFor, setSeededFor] = useState<string | typeof CLOSED>(() =>
    isOpen ? seedKey : CLOSED,
  );

  const effectiveKey = isOpen ? seedKey : CLOSED;
  if (effectiveKey === CLOSED) {
    if (seededFor !== CLOSED) setSeededFor(CLOSED);
  } else if (effectiveKey !== seededFor) {
    setSeededFor(effectiveKey);
    setState(buildState());
    onReseed?.();
  }

  return [state, setState];
}
